import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Zap, Copy, ArrowLeft, ArrowRight, CheckCircle2, Code2, MonitorSmartphone, KeyRound } from "lucide-react";
import { toast, Toaster } from "sonner";

function CodeBlock({ lang, code, testid }) {
  return (
    <div className="relative group" data-testid={testid}>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied"); }}
          data-testid={`${testid}-copy`}
          className="p-1.5 rounded bg-white/95 border border-[#114232]/15 hover:bg-white"
        >
          <Copy className="w-3.5 h-3.5 text-[#114232]" />
        </button>
      </div>
      <pre className="bg-[#0d3125] text-[#f4f2eb] text-[12.5px] font-mono leading-relaxed p-4 rounded-md overflow-x-auto">
        <code>{code}</code>
      </pre>
      <div className="absolute bottom-2 right-3 text-[10px] uppercase tracking-wider text-[#f4f2eb]/40 font-bold pointer-events-none">{lang}</div>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://your-app.example.com";

export default function EmbedDocs() {
  const { user } = useAuth();
  const [myKeys, setMyKeys] = useState([]);
  const [selectedPrefix, setSelectedPrefix] = useState("nks_live_XXXX");
  const [partnerSlug, setPartnerSlug] = useState("kcb-bank");

  useEffect(() => {
    if (!user) return;
    const isPartner = user.role === "partner_admin" || user.role === "super_admin";
    if (!isPartner) return;
    const path = user.role === "super_admin" ? "/admin/api-keys" : "/partner/api-keys";
    api.get(path)
      .then((r) => {
        const active = (r.data.items || []).filter((k) => !k.revoked_at);
        setMyKeys(active);
        if (active.length > 0) setSelectedPrefix(active[0].prefix);
      })
      .catch(() => { /* silent — anonymous viewers stay on placeholders */ });
  }, [user]);

  // Snippets — kept short so the page is easy to scan.
  const iframeSnippet =
`<iframe
  src="${BACKEND}/embed/signup?api_key=${selectedPrefix}…&partner_slug=${partnerSlug}"
  width="100%"
  height="780"
  style="border:0; max-width:560px;"
  title="Get cyber-cover by Nyoksure"
  allow="clipboard-write"
></iframe>`;

  const postMessageJs =
`// Listen for signup events from the embedded Nyoksure widget
window.addEventListener("message", (event) => {
  // Always validate event.origin in production
  if (!event.origin.endsWith(".emergentagent.com")) return;
  const m = event.data || {};
  if (m.type === "nyoksure:signup:success") {
    console.log("Merchant signed up:", m.user, m.access_token);
    // → forward access_token to your own auth layer, redirect, etc.
  } else if (m.type === "nyoksure:signup:error") {
    console.error("Signup failed:", m.error);
  }
});`;

  const curlSnippet =
`# Server-side signup — useful for native iOS/Android apps that
# don't render the iframe directly.
curl -X POST "${BACKEND}/api/public/embed/signup" \\
  -H "X-API-Key: ${selectedPrefix}…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "business_name": "Mama Wanjiru Boutique",
    "owner_name":    "Wanjiru Kamau",
    "phone":         "+254712345678",
    "email":         "wanjiru@example.co.ke",
    "password":      "ShopOwner@123",
    "business_type": "retail",
    "country":       "KE"
  }'`;

  const swiftSnippet =
`// Swift / WKWebView — drop the widget into a UIView
let url = URL(string:
  "${BACKEND}/embed/signup?api_key=\\\\(apiKey)&partner_slug=${partnerSlug}")!
webView.load(URLRequest(url: url))

// Bridge postMessage → native via WKScriptMessageHandler:
let userContentController = WKUserContentController()
userContentController.add(self, name: "nyoksure")
let inject = """
  window.addEventListener("message", (e) => {
    if (e.data?.type?.startsWith("nyoksure:")) {
      window.webkit.messageHandlers.nyoksure.postMessage(e.data);
    }
  });
"""
userContentController.addUserScript(
  WKUserScript(source: inject, injectionTime: .atDocumentEnd, forMainFrameOnly: false)
)`;

  const kotlinSnippet =
`// Kotlin / Android WebView
binding.webView.settings.javaScriptEnabled = true
binding.webView.addJavascriptInterface(NyoksureBridge(), "nyoksureBridge")
binding.webView.loadUrl(
  "${BACKEND}/embed/signup?api_key=$apiKey&partner_slug=${partnerSlug}"
)

binding.webView.evaluateJavascript("""
  window.addEventListener('message', (e) => {
    if (e.data && e.data.type && e.data.type.startsWith('nyoksure:')) {
      nyoksureBridge.onEvent(JSON.stringify(e.data));
    }
  });
""", null)

class NyoksureBridge {
  @JavascriptInterface
  fun onEvent(payload: String) { /* parse JSON, route to your auth layer */ }
}`;

  return (
    <div className="min-h-screen bg-[#f4f2eb]" data-testid="embed-docs-page">
      <Toaster position="top-right" />
      <nav className="px-6 lg:px-10 py-5 border-b border-[#114232]/8 max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="relative">
            <Shield className="w-7 h-7 text-[#114232]" strokeWidth={1.5} />
            <Zap className="w-3 h-3 text-[#c15337] absolute -bottom-0.5 -right-0.5" fill="#c15337" strokeWidth={0} />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight text-[#114232]">Nyoksure</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5c6360] hover:text-[#114232]">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>
      </nav>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <header className="space-y-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#c15337] font-bold">Developer docs</div>
          <h1 className="font-heading text-4xl font-bold text-[#114232]">Embed Nyoksure in 90 seconds.</h1>
          <p className="text-sm text-[#5c6360] max-w-2xl">
            Drop the signup widget into your bank app, fintech onboarding, or POS dashboard with one iframe.
            Listen to <code className="font-mono text-[#c15337]">postMessage</code> events to capture the new merchant's access token, then continue your own flow.
          </p>
        </header>

        {/* Step 1: API key */}
        <section className="space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#114232] text-white text-[11px] font-bold flex items-center justify-center">1</div>
            <h2 className="font-heading text-2xl font-bold text-[#114232] flex items-center gap-2">
              <KeyRound className="w-5 h-5" /> Get an API key
            </h2>
          </div>
          <p className="text-sm text-[#5c6360]">
            Partner admins can issue keys from the{" "}
            <Link to="/partner" className="underline text-[#114232]">partner portal</Link>.
            Nyoksure operators issue global keys from the{" "}
            <Link to="/security" className="underline text-[#114232]">admin security page</Link>.
            Each key is scope-gated and SHA-256 hashed at rest — the secret is shown <strong>only once</strong>.
          </p>
          {myKeys.length > 0 && (
            <div className="p-4 bg-white border border-[#114232]/15 rounded-md" data-testid="your-keys-block">
              <div className="text-[11px] uppercase tracking-wider text-[#5c6360] font-bold mb-2">Your active keys (click to use in snippets below)</div>
              <div className="flex flex-wrap gap-2">
                {myKeys.map((k) => (
                  <button
                    key={k.id}
                    onClick={() => setSelectedPrefix(k.prefix)}
                    data-testid={`docs-key-${k.id}`}
                    className={`text-xs font-mono px-2.5 py-1.5 rounded border ${selectedPrefix === k.prefix ? "border-[#c15337] bg-[#c15337]/10 text-[#8b3a23]" : "border-[#114232]/15 text-[#114232] hover:border-[#114232]/40"}`}
                  >
                    {k.prefix}… · {k.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Step 2: iframe */}
        <section className="space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#114232] text-white text-[11px] font-bold flex items-center justify-center">2</div>
            <h2 className="font-heading text-2xl font-bold text-[#114232] flex items-center gap-2">
              <Code2 className="w-5 h-5" /> Drop the iframe
            </h2>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-[11px] uppercase tracking-wider text-[#5c6360] font-bold">Partner slug:</label>
            <input
              value={partnerSlug}
              onChange={(e) => setPartnerSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              data-testid="docs-partner-slug-input"
              className="text-xs font-mono border border-[#114232]/20 px-2 py-1 rounded w-40"
            />
          </div>
          <CodeBlock lang="HTML" code={iframeSnippet} testid="snippet-iframe" />
        </section>

        {/* Step 3: postMessage */}
        <section className="space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#114232] text-white text-[11px] font-bold flex items-center justify-center">3</div>
            <h2 className="font-heading text-2xl font-bold text-[#114232] flex items-center gap-2">
              <MonitorSmartphone className="w-5 h-5" /> Capture the signup event
            </h2>
          </div>
          <CodeBlock lang="JavaScript" code={postMessageJs} testid="snippet-postmessage" />
        </section>

        {/* Server-side */}
        <section className="space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#c15337] text-white text-[11px] font-bold flex items-center justify-center">4</div>
            <h2 className="font-heading text-2xl font-bold text-[#114232]">Or call the API directly</h2>
          </div>
          <CodeBlock lang="cURL" code={curlSnippet} testid="snippet-curl" />
        </section>

        {/* Mobile snippets */}
        <section className="space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#c15337] text-white text-[11px] font-bold flex items-center justify-center">5</div>
            <h2 className="font-heading text-2xl font-bold text-[#114232]">Mobile (iOS / Android)</h2>
          </div>
          <CodeBlock lang="Swift" code={swiftSnippet} testid="snippet-swift" />
          <CodeBlock lang="Kotlin" code={kotlinSnippet} testid="snippet-kotlin" />
        </section>

        {/* Scopes table */}
        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-bold text-[#114232]">Available scopes</h2>
          <div className="bg-white border border-[#114232]/12 rounded-md overflow-hidden" data-testid="docs-scopes-table">
            <table className="w-full text-sm">
              <thead className="bg-[#114232]/5 text-[10px] uppercase tracking-wider text-[#5c6360]">
                <tr>
                  <th className="text-left py-3 px-5 font-bold">Scope</th>
                  <th className="text-left py-3 px-5 font-bold">What it grants</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-[#114232]/10">
                  <td className="py-3 px-5 font-mono text-xs text-[#114232]">signup:create</td>
                  <td className="py-3 px-5">Create a new merchant + auto-provisioned Merchant-Lite policy via <code>/api/public/embed/signup</code>.</td>
                </tr>
                <tr className="border-t border-[#114232]/10">
                  <td className="py-3 px-5 font-mono text-xs text-[#114232]">merchant:read</td>
                  <td className="py-3 px-5">Reserved — read merchant profile data (coming soon).</td>
                </tr>
                <tr className="border-t border-[#114232]/10">
                  <td className="py-3 px-5 font-mono text-xs text-[#114232]">policy:read</td>
                  <td className="py-3 px-5">Reserved — read policy + premium ledger (coming soon).</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#114232] text-[#f4f2eb] rounded-md p-8 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#d49a36] mb-1">Ready to integrate</div>
            <h3 className="font-heading text-2xl font-bold">Need a sandbox key or higher rate limits?</h3>
            <p className="text-sm text-[#f4f2eb]/70 mt-1">Email <code className="font-mono">partners@nyoksure.co.ke</code> — we onboard in 24 hrs.</p>
          </div>
          <a
            href="mailto:partners@nyoksure.co.ke"
            data-testid="docs-cta-email"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#c15337] text-white font-semibold hover:opacity-95"
          >
            Talk to partnerships <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </section>

        <footer className="text-[11px] text-[#5c6360] text-center pt-8 border-t border-[#114232]/8">
          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-[#114232]" />
          IRA-aligned · 7-day cooling-off · HMAC-signed webhooks
        </footer>
      </main>
    </div>
  );
}
